import { formatPercentBp, parsePence, penceToInput, percentToBp } from '@hcd/domain'
import { Loader2, Lock } from 'lucide-react'
import { useEffect, useId, useState, type ComponentProps, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

/** A labelled field. Pass the control as a render function to receive its id. */
export function FormField({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string
  hint?: ReactNode
  error?: string | null
  children: (props: {
    id: string
    'aria-describedby'?: string
    'aria-invalid'?: boolean
  }) => ReactNode
  className?: string
}) {
  const id = useId()
  const hintId = `${id}-hint`
  const errId = `${id}-err`
  const describedBy =
    [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined
  return (
    <div className={cn('grid content-start gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {hint && !error && (
        <p id={hintId} className="text-[13px] text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} className="text-[13px] font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}

export const fieldClass = 'h-11 text-base md:h-10 md:text-[15px]'

export function TextInput(props: ComponentProps<typeof Input>) {
  return <Input {...props} className={cn(fieldClass, props.className)} />
}

/** Native select: the phone's own picker is the best picker in the field. */
export function NativeSelect({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <select
      {...props}
      className={cn(
        'w-full rounded-md border border-input bg-card px-3 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 aria-invalid:border-destructive disabled:opacity-60',
        fieldClass,
        className,
      )}
    >
      {children}
    </select>
  )
}

/**
 * Money entry in pounds, stored as integer pence. Shows the typed text while
 * editing; reports null while the text is not a valid amount.
 */
export function MoneyInput({
  valuePence,
  onChangePence,
  ...props
}: Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'> & {
  valuePence: number | null
  onChangePence: (p: number | null, raw: string) => void
}) {
  const [raw, setRaw] = useState(valuePence === null ? '' : penceToInput(valuePence))
  useEffect(() => {
    // Re-sync when the value changes from outside (e.g. reset/reload).
    const parsed = parsePence(raw)
    if (valuePence !== null && parsed !== valuePence) setRaw(penceToInput(valuePence))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuePence])
  return (
    <div className="relative">
      <span
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground"
        aria-hidden
      >
        £
      </span>
      <Input
        {...props}
        inputMode="decimal"
        autoComplete="off"
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value)
          onChangePence(parsePence(e.target.value), e.target.value)
        }}
        onBlur={(e) => {
          const p = parsePence(raw)
          if (p !== null) setRaw(penceToInput(p))
          props.onBlur?.(e)
        }}
        className={cn(fieldClass, 'tabular pl-7', props.className)}
      />
    </div>
  )
}

export function PercentInput({
  valueBp,
  onChangeBp,
  ...props
}: Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'> & {
  valueBp: number | null
  onChangeBp: (bp: number | null) => void
}) {
  const [raw, setRaw] = useState(valueBp === null ? '' : formatPercentBp(valueBp).replace('%', ''))
  return (
    <div className="relative">
      <Input
        {...props}
        inputMode="decimal"
        autoComplete="off"
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value)
          onChangeBp(percentToBp(e.target.value))
        }}
        className={cn(fieldClass, 'tabular pr-8', props.className)}
      />
      <span
        className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground"
        aria-hidden
      >
        %
      </span>
    </div>
  )
}

export function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  const id = useId()
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="grid gap-0.5">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {description && <p className="text-[13px] text-muted-foreground">{description}</p>}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className="mt-0.5"
      />
    </div>
  )
}

/** Sticky save bar: appears only when there are unsaved changes. */
export function SaveBar({
  dirty,
  saving,
  valid,
  onSave,
  onDiscard,
}: {
  dirty: boolean
  saving: boolean
  valid: boolean
  onSave: () => void
  onDiscard: () => void
}) {
  if (!dirty) return null
  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className="sticky bottom-20 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3 shadow-lg md:bottom-4"
    >
      <p className="text-sm font-medium">
        {valid ? 'You have unsaved changes.' : 'Fix the highlighted fields to save.'}
      </p>
      <div className="flex gap-2">
        <Button variant="outline" className="h-10" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button className="h-10" onClick={onSave} disabled={saving || !valid}>
          {saving && <Loader2 className="animate-spin" />}
          Save changes
        </Button>
      </div>
    </div>
  )
}

export function ReadOnlyNote({ who }: { who: string }) {
  return (
    <p className="mb-4 flex items-center gap-2 rounded-lg border bg-muted px-3 py-2 text-sm text-muted-foreground">
      <Lock className="size-4 shrink-0" aria-hidden />
      Only {who} can change these settings.
    </p>
  )
}

export function SettingsCard({
  title,
  description,
  children,
  action,
}: {
  title: string
  description?: ReactNode
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <section className="rounded-xl border bg-card">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          {description && (
            <p className="mt-0.5 max-w-[65ch] text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {action}
      </header>
      <div className="grid gap-5 p-5">{children}</div>
    </section>
  )
}

/** Map zod issues to { fieldName: message }. */
export function issuesToErrors(
  issues: { path: PropertyKey[]; message: string }[],
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const i of issues) {
    const k = String(i.path[0] ?? '')
    if (k && !out[k]) out[k] = i.message
  }
  return out
}
